/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import {
    Box,
    Typography,
    Modal,
    Fade,
    Backdrop,
    useTheme,
    Alert,
    IconButton,
    Stack,
    TextField,
    InputAdornment,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    TableFooter,
    TablePagination,
    Tooltip,
} from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { Asset, Effect, ModalBase, Nullable, Setter, User } from "../../utils/Interfaces";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { selectExistingAvailableAssets } from "./assetsSlice";
import { delayedAction } from "../../utils/Functions";
import AddIcon from "@mui/icons-material/Add";
import { assignAsset, updateAvailableAssets } from "./assetsFunctions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

interface AssetAddTableCellProps {
    asset: Asset;
    user: User;
    onAddFinished: Setter<Effect>;
}

const AssetAddTableCell = (props: AssetAddTableCellProps) => {
    const { user, asset, onAddFinished } = props;

    const [loading, setLoading] = useState<boolean>(false);

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    return (
        <TableCell sx={{ pl: 1 }}>
            <Stack direction="row" justifyContent="flex-start">
                {loading && <CircularProgress size={16} sx={{ ml: 0.4, mt: 0.5, mr: 1.6 }} />}
                {!loading && (
                    <Tooltip title={`Assign ${asset.name} to ${user.first_name} ${user.last_name}`}>
                        <IconButton
                            color="success"
                            size="small"
                            sx={{ p: 0, mr: 1 }}
                            onClick={() => {
                                setLoading(true);
                                assignAsset(
                                    user.id,
                                    asset.id,
                                    dispatch,
                                    () => onAddFinished(() => setLoading(false)),
                                    () => {}
                                );
                            }}
                        >
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <Typography>{asset.name}</Typography>
            </Stack>
        </TableCell>
    );
};

export interface AssignAssetModalProps extends ModalBase {
    user: Nullable<User>;
    rowsPerPage: number;
    onAssetAssignedToUser: Setter<Effect>;
}

const AssignAssetModal = (props: AssignAssetModalProps) => {
    const { open, handleClose, user, rowsPerPage, onAssetAssignedToUser } = props;
    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const theme = useTheme();

    const [page, setPage] = useState<number>(0);
    const [assetSearchQuery, setAssetSearchQuery] = useState<string>("");

    const [assetResults, setAssetResults] = useState<Asset[]>([]);

    const availableAssets: Asset[] = useAppSelector(selectExistingAvailableAssets);

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            setAssetSearchQuery("");
        });
    }

    const handleChangePage = (event: any, newPage: number) => {
        setPage(newPage);
    };

    useEffect(() => {
        let results: Asset[] = availableAssets.filter((asset: Asset) => {
            return (
                asset.name.toLowerCase().includes(assetSearchQuery.toLowerCase()) ||
                asset.part_number.toLowerCase().includes(assetSearchQuery.toLowerCase()) ||
                (asset.serial_number !== null && asset.serial_number.toLowerCase().includes(assetSearchQuery.toLowerCase()))
            );
        });
        setPage(0);
        setAssetResults(results);
    }, [user, availableAssets, assetSearchQuery]);

    if (!user) {
        return (
            <Box>
                <Alert severity="warning">
                    <Typography>Select a user to continue.</Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        width: "100%",
                        maxHeight: "90vh",
                        maxWidth: "80%",
                        backgroundColor: theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: theme.shadows[5],
                        padding: theme.spacing(5, 4, 3),
                        margin: theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        overflowY: "auto",
                        position: "relative",
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: -2, mb: 2 }}>
                        <Typography variant="h5">Available Assets</Typography>
                        <Stack direction="column" justifyContent="flex-start" sx={{ mt: -2, mr: -2 }}>
                            <IconButton aria-label="delete" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </Stack>
                    <Stack direction="column" spacing={2}>
                        <TextField
                            id="asset-search"
                            label="Search Assets"
                            type="search"
                            variant="outlined"
                            size="small"
                            onChange={(e) => setAssetSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TableContainer component={Paper}>
                            <Table size="small" aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ pl: 5, width: "40%" }}>
                                            <Typography fontWeight="bold">Asset Name</Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: "30%" }} align="left">
                                            <Typography fontWeight="bold">Part #</Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: "30%" }} align="left">
                                            <Typography fontWeight="bold">Serial #</Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow
                                        key="loader"
                                        sx={{
                                            "&:last-child td, &:last-child th": { border: 0 },
                                        }}
                                    >
                                        <TableCell colSpan={2}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    p: 1,
                                                }}
                                            >
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {assetResults.length < 1 && (
                                        <TableRow
                                            key="idle-text"
                                            sx={{ "&:last-child td, &:last-child th": { border: 0 }, opacity: 0.5 }}
                                        >
                                            <TableCell colSpan={3}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        p: 1,
                                                    }}
                                                >
                                                    No results found.
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {assetResults
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row: Asset, index: number) => (
                                            <TableRow key={index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                <AssetAddTableCell
                                                    asset={row}
                                                    user={user}
                                                    onAddFinished={(depCallback: Effect) => {
                                                        updateAvailableAssets(dispatch, () => {});
                                                        onAssetAssignedToUser(depCallback);
                                                        onClose();
                                                    }}
                                                />
                                                <TableCell align="left">{row.part_number}</TableCell>
                                                <TableCell align="left">{row.serial_number}</TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            colSpan={3}
                                            count={assetResults.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            labelRowsPerPage=""
                                            onPageChange={handleChangePage}
                                            showFirstButton={false}
                                            showLastButton={false}
                                            SelectProps={{ sx: { display: "none" }, defaultValue: 25 }}
                                        />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    );
};

export default AssignAssetModal;
